import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface RecentShortcut {
  keys: string[];
  description: string;
  lastUsed: number;
  useCount: number;
}

interface KeyboardShortcutsModalState {
  isOpen: boolean;
  recentShortcuts: RecentShortcut[];
  open: () => void;
  close: () => void;
  toggle: () => void;
  trackShortcutUsage: (keys: string[], description: string) => void;
  clearRecentShortcuts: () => void;
}

const MAX_RECENT_SHORTCUTS = 10;

/**
 * Store for managing keyboard shortcuts modal state and tracking usage
 */
export const useKeyboardShortcutsModal = create<KeyboardShortcutsModalState>()(
  persist(
    (set) => ({
      isOpen: false,
      recentShortcuts: [],

      open: () => set({ isOpen: true }),

      close: () => set({ isOpen: false }),

      toggle: () => set((state) => ({ isOpen: !state.isOpen })),

      trackShortcutUsage: (keys: string[], description: string) =>
        set((state) => {
          const keyString = keys.join('+');
          const existingIndex = state.recentShortcuts.findIndex(
            (s) => s.keys.join('+') === keyString
          );

          let updatedRecent: RecentShortcut[];

          if (existingIndex >= 0) {
            // Update existing shortcut
            updatedRecent = [...state.recentShortcuts];
            updatedRecent[existingIndex] = {
              ...updatedRecent[existingIndex],
              lastUsed: Date.now(),
              useCount: updatedRecent[existingIndex].useCount + 1,
            };
          } else {
            // Add new shortcut
            updatedRecent = [
              {
                keys,
                description,
                lastUsed: Date.now(),
                useCount: 1,
              },
              ...state.recentShortcuts,
            ];
          }

          // Sort by use count (descending), then by last used (descending)
          updatedRecent.sort((a, b) => {
            if (b.useCount !== a.useCount) {
              return b.useCount - a.useCount;
            }
            return b.lastUsed - a.lastUsed;
          });

          // Keep only top MAX_RECENT_SHORTCUTS
          updatedRecent = updatedRecent.slice(0, MAX_RECENT_SHORTCUTS);

          return { recentShortcuts: updatedRecent };
        }),

      clearRecentShortcuts: () => set({ recentShortcuts: [] }),
    }),
    {
      name: 'keyboard-shortcuts-storage',
      partialize: (state) => ({
        recentShortcuts: state.recentShortcuts,
      }),
    }
  )
);
